import React, { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import { usePermissionStore } from '../../store/usePermissionStore';
import { getResourceFromPath } from '../../utils/permissions';

interface PermissionProtectedRouteProps {
  children: ReactNode;
  resource?: string;
  requiredAction?: string;
}

const PermissionProtectedRoute: React.FC<PermissionProtectedRouteProps> = ({
  children,
  resource,
  requiredAction = 'read',
}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const permissions = usePermissionStore((state) => state.permissions);
  const isLoading = usePermissionStore((state) => state.isLoading);
  const hasFetched = usePermissionStore((state) => state.hasFetched);
  const isMainAdmin = usePermissionStore((state) => state.isMainAdmin);
  const hasPermission = usePermissionStore((state) => state.hasPermission);
  const fetchPermissions = usePermissionStore((state) => state.fetchPermissions);
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && !hasFetched && !isLoading) {
      fetchPermissions();
    }
  }, [isAuthenticated, hasFetched, isLoading, fetchPermissions]);

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  // Wait for permissions to load
  if (isLoading || !hasFetched) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-lg text-gray-600">Loading permissions...</div>
        </div>
      </div>
    );
  }

  // Main admin has access to everything
  if (isMainAdmin) {
    return <>{children}</>;
  }

  // Determine resource from path if not provided
  const resourceToCheck = resource || getResourceFromPath(location.pathname);

  // If no resource mapping, allow access (for pages that don't need permissions)
  if (!resourceToCheck) {
    return <>{children}</>;
  }

  // Check if user has permission
  // Don't redirect - let the page component handle showing access denied message
  if (!hasPermission(resourceToCheck, requiredAction)) {
    // Still render children so the page can show its own access denied UI
    return <>{children}</>;
  }

  return <>{children}</>;
};

export default PermissionProtectedRoute;

